/**
 * Mapbox configuration.
 *
 * Validates tokens at startup and exposes a clean config object
 * for all map components to consume.
 */

import type { BasemapStyle } from '../types/mapTypes';

// ─── Token ──────────────────────────────────────────────────────────

const ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

/**
 * Validate that the Mapbox token is available.
 * Does NOT log the token itself for security.
 */
export function validateMapboxConfig(): { valid: boolean; error?: string } {
  if (!ACCESS_TOKEN) {
    return {
      valid: false,
      error:
        'Mapbox access token is not configured. ' +
        'Set REACT_APP_MAPBOX_ACCESS_TOKEN in your .env file.',
    };
  }

  // Basic format check — Mapbox tokens start with "pk." for public tokens
  if (!ACCESS_TOKEN.startsWith('pk.')) {
    return {
      valid: false,
      error:
        'The Mapbox token does not look like a valid public access token ' +
        '(should start with "pk.").',
    };
  }

  return { valid: true };
}

export function getMapboxToken(): string {
  return ACCESS_TOKEN;
}

// ─── Basemap styles ─────────────────────────────────────────────────

export const BASEMAP_STYLES: Record<BasemapStyle, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  'satellite-labels': 'mapbox://styles/mapbox/satellite-streets-v12',
  terrain: 'mapbox://styles/mapbox/outdoors-v12',
};

// ─── Default viewport ───────────────────────────────────────────────

/** Kigali, Rwanda center */
export const RWANDA_CENTER: [number, number] = [30.0619, -1.9441]; // [lng, lat]

/** Country-level zoom */
export const RWANDA_ZOOM = 8.5;

/** Operational zoom */
export const FIELD_ZOOM = 15;

/** Tree detail zoom */
export const TREE_ZOOM = 19;

// ─── Performance ────────────────────────────────────────────────────

export const MIN_ZOOM = 6;
export const MAX_ZOOM = 22;

/** Debounce delay for viewport-triggered queries (ms) */
export const VIEWPORT_DEBOUNCE_MS = 300;

/** Debounce delay for search input (ms) */
export const SEARCH_DEBOUNCE_MS = 250;

/** Maximum features per layer request */
export const LAYER_FEATURE_LIMIT = 5000;

// ─── Mapbox restrictions ────────────────────────────────────────────

/**
 * Mapbox token restrictions for production use.
 * These should be set in the Mapbox console:
 *   - URL restriction: https://*.esanza.com/*
 *   - Scopes: styles:read, fonts:read, tiles:read
 */
export const TOKEN_RESTRICTIONS = {
  urlPattern: 'https://*.esanza.com/*',
  scopes: ['styles:read', 'fonts:read', 'tiles:read'],
};

// ─── Rwanda bounds ──────────────────────────────────────────────────

/** Bounding box for Rwanda: [west, south, east, north] in [lng, lat] */
export const RWANDA_BOUNDS: [number, number, number, number] = [
  28.8573, // west
  -2.8404, // south
  30.8927, // east
  -1.0471, // north
];
